import React, { useEffect, useRef, useState, useContext } from 'react';
import {
    Container,
    Typography,
    Button,
    Box,
    Paper,
    CircularProgress,
    Alert,
    RadioGroup,
    Radio,
    LinearProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import { whatsappAccountService } from '../../services/api';
import AppLayout from '../layout/AppLayout';
import { INK, MUTED, RULE } from '../analytics/analyticsUi';
import axios from 'axios';

// Get API URL from environment variable
const API_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;

// Runtime config from the backend (/env-config.js),
// falling back to build-time REACT_APP_* vars when running with `npm start`
const envValue = (key) => ((window._env_ ? window._env_[key] : process.env[`REACT_APP_${key}`]) || '').toString().trim();

const FACEBOOK_APP_ID = envValue('FACEBOOK_APP_ID');
const WHATSAPP_CONFIG_ID = envValue('WHATSAPP_CONFIG_ID');

// Partner Solution onboarding
const PARTNER_SOLUTION_ENABLED = window._env_
    ? window._env_.PARTNER_SOLUTION_ENABLED === true
    : process.env.REACT_APP_PARTNER_SOLUTION_ENABLED === 'true';
const PARTNER_SOLUTION_ID = envValue('PARTNER_SOLUTION_ID');
const USE_PARTNER_SOLUTION = PARTNER_SOLUTION_ENABLED && !!PARTNER_SOLUTION_ID;
// Partner mode may use its own Embedded Signup configuration; defaults to the standard one
const SIGNUP_CONFIG_ID = (USE_PARTNER_SOLUTION && envValue('PARTNER_CONFIG_ID')) || WHATSAPP_CONFIG_ID;

const DARK_GREEN = '#0D7332';
const SELECTED_BG = 'rgba(37,211,102,0.08)';

const FLOW_OPTIONS = [
    {
        value: 'standard',
        title: 'New WhatsApp Business account',
        description: 'Create a WhatsApp Business account, or connect one you already use with the API.'
    },
    {
        value: 'business_app',
        title: 'Existing WhatsApp Business app',
        description: 'Connect the number you use in the WhatsApp Business app. Keep using the app for 1:1 chats while you send business messages through our API.'
    }
];

const REQUIREMENTS = [
    'A display name for your WhatsApp profile',
    'Your legal business name',
    'Your business address',
    'A Facebook account that manages your business'
];

const BUSINESS_APP_REQUIREMENTS = [
    'WhatsApp Business app version 2.24.17 or higher',
    'Two-step verification turned off',
    'Business verification, recommended for higher messaging limits',
    'You can use the app and our API at the same time'
];

const FlowOption = ({ option, selected }) => {
    const inputId = `flow-option-${option.value}`;
    return (
        <Box
            component="label"
            htmlFor={inputId}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: { xs: 1.5, sm: 2 },
                pl: { xs: 0.5, sm: 1 },
                border: '1px solid',
                borderColor: selected ? 'primary.main' : RULE,
                boxShadow: (theme) => (selected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : 'none'),
                bgcolor: selected ? SELECTED_BG : 'background.paper',
                borderRadius: 2,
                cursor: 'pointer',
                height: '100%',
                transition: 'border-color 150ms ease, background-color 150ms ease',
                '&:hover': { borderColor: selected ? 'primary.main' : MUTED },
                '&:focus-within': {
                    outline: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                    outlineOffset: 2
                }
            }}
        >
            <Radio
                id={inputId}
                value={option.value}
                sx={{ mt: -0.5 }}
                inputProps={{ 'aria-describedby': `${inputId}-desc` }}
            />
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, color: INK, lineHeight: 1.35 }}>
                    {option.title}
                </Typography>
                <Typography id={`${inputId}-desc`} variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                    {option.description}
                </Typography>
            </Box>
        </Box>
    );
};

const WhatsAppOnboarding = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // 'idle' | 'received' (signup finished, waiting for authorization code) | 'authenticating' | 'saving'
    const [progress, setProgress] = useState('idle');
    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [pendingAccountData, setPendingAccountData] = useState(null);
    const [manualUserData, setManualUserData] = useState(null);
    const [flowType, setFlowType] = useState('standard'); // 'standard' or 'business_app'

    // Latest Facebook SDK login response. A ref, because the message listener is
    // registered once on mount and would otherwise only ever see the initial value.
    const sdkResponseRef = useRef(null);

    // Manual user loading when we have token but no user data
    useEffect(() => {
        const loadUserManually = async () => {
            try {
                const token = localStorage.getItem('token');

                if (token && !user) {
                    const config = {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    };

                    try {
                        const response = await axios.get(`${API_URL}/auth/me`, config);

                        if (response.data && response.data.data) {
                            setManualUserData(response.data.data);
                        } else {
                            setError('We could not read your account details. Refresh the page and try again.');
                        }
                    } catch (apiError) {
                        console.error('Loading user failed:', apiError.message);
                        if (apiError.response?.status === 401) {
                            setError('Your session has expired. Log in again to continue.');
                        } else {
                            setError(`Could not load your account: ${apiError.message}`);
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading user:', err);
                setError('Could not load your account. Refresh the page and try again.');
            }
        };

        loadUserManually();
    }, [user]);

    useEffect(() => {
        // Check authentication status
        if (!authLoading && !isAuthenticated) {
            setError('Log in to connect your WhatsApp Business account.');
        }

        // If we have pending account data and user is now authenticated, process it
        if (pendingAccountData && (user?._id || manualUserData?._id)) {
            const { phoneNumberId, wabaId, authCode, isBusinessApp } = pendingAccountData;
            const userId = user?._id || manualUserData?._id;
            processSaveWhatsAppAccount(phoneNumberId, wabaId, authCode, userId, isBusinessApp);
            setPendingAccountData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, manualUserData, isAuthenticated, authLoading, pendingAccountData]);

    const processSaveWhatsAppAccount = async (phoneNumberId, wabaId, authCode, userId, isBusinessApp = false) => {
        try {
            setLoading(true);
            setError(null);
            setProgress('saving');

            const accountData = {
                phoneNumberId,
                wabaId,
                userId,
                authCode,
                isBusinessApp
            };

            if (isBusinessApp) {
                // For business app onboarding, use a different endpoint
                await whatsappAccountService.connectBusinessAppAccount(accountData);
            } else {
                // Standard flow
                await whatsappAccountService.connectWhatsAppAccount(accountData);
            }

            setLoading(false);
            navigate('/whatsapp-accounts');
        } catch (err) {
            console.error('Saving WhatsApp account failed:', err?.message);
            setError(err.response?.data?.error || 'Could not save your WhatsApp account. Try connecting again.');
            setLoading(false);
            setProgress('idle');
        }
    };

    const saveWhatsAppAccount = async (phoneNumberId, wabaId, isBusinessApp = false) => {
        // Get the authorization code from window.whatsappSessionData if available
        const authCode = window.whatsappSessionData?.authCode ||
            sdkResponseRef.current?.authResponse?.code || null;

        // Check if user is authenticated - use either context user or manually loaded user
        const userId = user?._id || manualUserData?._id;

        if (!userId) {
            // Saved once authentication completes (see effect above)
            setLoading(true);
            setProgress('authenticating');
            setPendingAccountData({ phoneNumberId, wabaId, authCode, isBusinessApp });
            return;
        }

        // User is authenticated, proceed with saving
        await processSaveWhatsAppAccount(phoneNumberId, wabaId, authCode, userId, isBusinessApp);
    };

    // Always points at the latest saveWhatsAppAccount so the mount-time listener
    // uses current user data.
    const saveWhatsAppAccountRef = useRef(saveWhatsAppAccount);
    saveWhatsAppAccountRef.current = saveWhatsAppAccount;

    useEffect(() => {
        const handleWhatsAppMessage = (event) => {
            if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') {
                return;
            }

            try {
                const data = JSON.parse(event.data);

                if (data.type === 'WA_EMBEDDED_SIGNUP') {
                    console.log('Embedded signup event:', data.event);
                    const sdkResponse = sdkResponseRef.current;

                    // Handle standard embedded signup completion
                    if (data.event === 'FINISH') {
                        const { phone_number_id, waba_id, business_id } = data.data;
                        setError(null);

                        // The Facebook SDK callback and the embedded signup message can arrive in any order
                        if (sdkResponse && sdkResponse.authResponse && sdkResponse.authResponse.code) {
                            // If we already have the SDK response, save immediately
                            saveWhatsAppAccountRef.current(phone_number_id, waba_id, false);
                        } else {
                            // Otherwise, keep the session data and wait for the SDK response.
                            // saveWhatsAppAccount will be called from fbLoginCallback
                            window.whatsappSessionData = {
                                phoneNumberId: phone_number_id,
                                wabaId: waba_id,
                                businessId: business_id,
                                isBusinessApp: false
                            };
                            setProgress('received');
                        }
                    }
                    // Handle WhatsApp Business app onboarding completion
                    else if (data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
                        const { waba_id } = data.data;
                        setError(null);

                        // For business app onboarding, we don't get phone_number_id in the event
                        if (sdkResponse && sdkResponse.authResponse && sdkResponse.authResponse.code) {
                            saveWhatsAppAccountRef.current(null, waba_id, true);
                        } else {
                            window.whatsappSessionData = {
                                phoneNumberId: null,
                                wabaId: waba_id,
                                businessId: null,
                                isBusinessApp: true
                            };
                            setProgress('received');
                        }
                    }
                    // User cancels the Embedded Signup flow
                    else if (data.event === 'CANCEL') {
                        const { current_step } = data.data;
                        setProgress('idle');
                        setError(`Signup was cancelled at step: ${current_step}. Continue with Facebook to start again.`);
                    }
                    // User reports an error during the Embedded Signup flow
                    else if (data.event === 'ERROR') {
                        const { error_message } = data.data;
                        setProgress('idle');
                        setError(`Meta reported an error during signup: ${error_message}`);
                    }
                }
            } catch (err) {
                // Non-JSON messages from Facebook are expected; ignore them
            }
        };

        // Load the Facebook SDK
        // Define the fbAsyncInit function before loading the SDK
        window.fbAsyncInit = function () {
            window.FB.init({
                appId: FACEBOOK_APP_ID,
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v22.0'
            });
            setSdkLoaded(true);
        };

        // Load the SDK asynchronously
        (function (d, s, id) {
            var js, fjs = d.getElementsByTagName(s)[0];
            if (d.getElementById(id)) return;
            js = d.createElement(s); js.id = id;
            js.src = 'https://connect.facebook.net/en_US/sdk.js';
            js.crossOrigin = 'anonymous';
            js.async = true;
            fjs.parentNode.insertBefore(js, fjs);
        }(document, 'script', 'facebook-jssdk'));

        // SDK script already on the page from an earlier visit
        if (window.FB) {
            setSdkLoaded(true);
        }

        // Setup event listener for WhatsApp embedded signup messages
        window.addEventListener('message', handleWhatsAppMessage);

        return () => {
            window.removeEventListener('message', handleWhatsAppMessage);
        };
    }, []);

    const fbLoginCallback = (response) => {
        sdkResponseRef.current = response;

        if (response.authResponse) {
            const code = response.authResponse.code;

            // If we already have the session data from the embedded signup
            if (window.whatsappSessionData) {
                const { phoneNumberId, wabaId, isBusinessApp } = window.whatsappSessionData;
                // Store the code in the window object so it's available for saveWhatsAppAccount
                window.whatsappSessionData.authCode = code;
                saveWhatsAppAccountRef.current(phoneNumberId, wabaId, isBusinessApp);
                // Clear the session data after use
                window.whatsappSessionData = null;
            }
        } else {
            console.log('Facebook login was cancelled or not fully authorized.');
            if (!window.whatsappSessionData) {
                setProgress('idle');
            }
        }
    };

    const launchWhatsAppSignup = () => {
        if (!FACEBOOK_APP_ID || !SIGNUP_CONFIG_ID) {
            setError('WhatsApp signup is not configured. The Facebook app ID or configuration ID is missing.');
            return;
        }

        if (!window.FB) {
            setError('The Facebook SDK has not loaded. Refresh the page and try again.');
            return;
        }

        setError(null);
        setProgress('idle');
        sdkResponseRef.current = null;

        const isBusinessAppFlow = flowType === 'business_app';

        const extras = USE_PARTNER_SOLUTION
            ? {
                setup: { solutionID: PARTNER_SOLUTION_ID },
                featureType: isBusinessAppFlow ? 'whatsapp_business_app_onboarding' : '',
                ...(isBusinessAppFlow ? {} : { features: ['marketing_messages_lite'] }),
                sessionInfoVersion: '3',
            }
            : {
                setup: {},
                featureType: isBusinessAppFlow ? 'whatsapp_business_app_onboarding' : '',
                sessionInfoVersion: isBusinessAppFlow ? '3' : '2',
            };

        // Launch Facebook login for WhatsApp Business onboarding
        window.FB.login(fbLoginCallback, {
            config_id: SIGNUP_CONFIG_ID,
            response_type: 'code', // Must be set to 'code' for System User access token
            override_default_response_type: true,
            extras
        });
    };

    const handleFlowTypeChange = (event) => {
        setFlowType(event.target.value);
        setError(null); // Clear any existing errors when switching flow types
    };

    const busy = loading || progress !== 'idle';

    const progressText = {
        received: 'Signup finished. Waiting for Facebook to confirm authorization…',
        authenticating: 'Signup finished. Confirming your portal login…',
        saving: 'Saving your account…'
    }[progress];

    return (
        <AppLayout>
            <Container maxWidth="md" sx={{ pb: 6 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        component="h1"
                        sx={{
                            fontSize: { xs: '1.75rem', md: '2.25rem' },
                            fontWeight: 700,
                            letterSpacing: '-0.025em',
                            color: INK,
                            lineHeight: 1.15
                        }}
                    >
                        Connect a WhatsApp number
                    </Typography>
                    <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
                        Sign in with Facebook to link your WhatsApp Business account, then send messages through our API.
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={busy ? undefined : () => setError(null)}>
                        {error}
                    </Alert>
                )}

                {progressText && (
                    <Paper
                        variant="outlined"
                        role="status"
                        aria-live="polite"
                        sx={{ borderColor: RULE, borderRadius: 2, mb: 2, overflow: 'hidden' }}
                    >
                        <LinearProgress />
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleOutlineIcon sx={{ color: DARK_GREEN }} />
                            <Typography sx={{ color: INK, fontWeight: 500 }}>{progressText}</Typography>
                        </Box>
                    </Paper>
                )}

                {/* Flow type */}
                <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, p: { xs: 2, sm: 3 }, mb: 2 }}>
                    <Typography id="flow-type-label" component="h2" sx={{ fontWeight: 600, color: INK, fontSize: '1.05rem' }}>
                        What are you connecting?
                    </Typography>
                    <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, mb: 2 }}>
                        Pick the option that matches your number today.
                    </Typography>

                    <RadioGroup
                        aria-labelledby="flow-type-label"
                        name="flow-type"
                        value={flowType}
                        onChange={handleFlowTypeChange}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 1.5
                        }}
                    >
                        {FLOW_OPTIONS.map((option) => (
                            <FlowOption key={option.value} option={option} selected={flowType === option.value} />
                        ))}
                    </RadioGroup>

                    {flowType === 'business_app' && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#F6F8FA',
                                border: `1px solid ${RULE}`
                            }}
                        >
                            <Typography sx={{ fontWeight: 600, color: INK, fontSize: '0.9rem', mb: 1 }}>
                                Requirements for connecting the Business app
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5, color: MUTED }}>
                                {BUSINESS_APP_REQUIREMENTS.map((item) => (
                                    <Typography key={item} component="li" variant="body2" sx={{ color: MUTED, py: 0.25 }}>
                                        {item}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Paper>

                {/* Requirements + action */}
                <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, p: { xs: 2, sm: 3 } }}>
                    <Typography component="h2" sx={{ fontWeight: 600, color: INK, fontSize: '1.05rem', mb: 1.5 }}>
                        What you'll need
                    </Typography>
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
                        {REQUIREMENTS.map((item) => (
                            <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 20, color: DARK_GREEN, mt: '1px' }} aria-hidden />
                                <Typography variant="body2" sx={{ color: INK }}>{item}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {USE_PARTNER_SOLUTION && (
                        <Alert severity="info" sx={{ mt: 2.5 }}>
                            You'll onboard through our Meta Partner Solution. Your WhatsApp Business account is shared with us as a solution partner.
                        </Alert>
                    )}

                    <Box
                        sx={{
                            mt: 3,
                            pt: 2.5,
                            borderTop: `1px solid ${RULE}`,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 1.5
                        }}
                    >
                        <Typography variant="body2" sx={{ color: MUTED }} aria-live="polite">
                            {sdkLoaded
                                ? 'A Facebook window opens to finish setup.'
                                : 'Loading Facebook sign-in…'}
                        </Typography>
                        <Button
                            onClick={launchWhatsAppSignup}
                            variant="contained"
                            color="primary"
                            disableElevation
                            disabled={loading || !sdkLoaded}
                            startIcon={loading || !sdkLoaded ? <CircularProgress size={18} color="inherit" /> : null}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1.25,
                                px: 3,
                                borderRadius: 2,
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: DARK_GREEN }
                            }}
                        >
                            {loading ? 'Saving your account…' : 'Continue with Facebook'}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </AppLayout>
    );
};

export default WhatsAppOnboarding;
